import { getStringWidth } from '@visx/text';
import { X_AXIS_FONT_SIZE, X_AXIS_TICK_SPACING } from '../utils';
import { Group } from '@visx/group';
import { formatterCurrencyTruncated } from '@/utilities/format';
import type { FairValueHistogramBucket } from '../fairValueHistogramFragment';
import type { ScaleBand } from 'd3-scale';
import type { CurrencyISO } from '@/constants/currencies';
import type { D3Scale } from '@visx/scale';
import { AxisBottom } from '@visx/axis';
import { isNumber } from 'lodash';
import { useMemo } from 'react';

type Props = {
  yMax: number;
  buckets: FairValueHistogramBucket[];
  xBandScale: ScaleBand<number>;
  currencyIso: CurrencyISO;
};

export const FairValueHistogramChartXAxis = ({
  yMax,
  buckets,
  xBandScale,
  currencyIso,
}: Props) => {
  const numBuckets = buckets.length;
  const xMax = xBandScale.range()[1];
  const maximumValue = buckets.at(-1)?.max ?? 0;
  const formatValue = (value: number) =>
    formatterCurrencyTruncated(value, currencyIso, 0);
  const maxTickWidth =
    getStringWidth(formatValue(maximumValue), {
      fontSize: X_AXIS_FONT_SIZE,
    }) ?? 0;

  const maxTicks = Math.floor(xMax / (maxTickWidth + X_AXIS_TICK_SPACING));
  const tickValues = useMemo(
    () =>
      buckets
        .filter((_, i) => {
          return (
            i === 0 ||
            i === numBuckets - 1 ||
            maxTicks >= 10 ||
            (maxTicks >= 5 && i % 3 === 0)
          );
        })
        .map((bucket) => bucket.min),
    [buckets, numBuckets, maxTicks]
  );

  return (
    <Group top={yMax}>
      <AxisBottom
        top={0}
        stroke="var(--s-color-text-subtle)"
        tickStroke="none"
        scale={xBandScale as D3Scale<number, number>}
        tickFormat={(value) => (isNumber(value) ? formatValue(value) : '')}
        tickValues={tickValues}
        tickLabelProps={(_, index) => ({
          className: 'fill-subtle',
          fontSize: X_AXIS_FONT_SIZE,
          textAnchor: 'start',
          x: xBandScale(tickValues[index]),
          transform: `translate(0, -4)`,
        })}
      />
    </Group>
  );
};
